import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";

const LOGO_URL = "https://snowsproam.com/logo512.png";

const pageStyle = {
  minHeight: "100vh",
  background:
    "radial-gradient(circle at top, rgba(12,105,200,0.18), transparent 36%), #0A0A0A",
  color: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif",
  boxSizing: "border-box",
};

const dialogStyle = {
  width: "100%",
  maxWidth: 480,
  background: "#141414",
  border: "1px solid #263044",
  borderRadius: 18,
  boxShadow: "0 28px 90px rgba(0,0,0,0.42)",
  overflow: "hidden",
  textAlign: "center",
  outline: "none",
};

const contentStyle = {
  padding: "30px 22px 28px",
};

const logoStyle = {
  width: 64,
  height: 64,
  objectFit: "contain",
  display: "block",
  margin: "0 auto 12px",
};

const statusBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 34,
  padding: "0 13px",
  borderRadius: 999,
  background: "rgba(12,105,200,0.16)",
  border: "1px solid rgba(96,165,250,0.32)",
  color: "#93C5FD",
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: 1,
  textTransform: "uppercase",
  marginBottom: 16,
};

const errorBadgeStyle = {
  ...statusBadgeStyle,
  background: "rgba(239,68,68,0.14)",
  border: "1px solid rgba(248,113,113,0.32)",
  color: "#FCA5A5",
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: 46,
  padding: "0 22px",
  borderRadius: 10,
  background: "#0C69C8",
  color: "#FFFFFF",
  textDecoration: "none",
  fontWeight: 800,
  border: "1px solid #0C69C8",
};

const supportLinkStyle = {
  color: "#60A5FA",
  textDecoration: "none",
  fontWeight: 800,
};

export default function AuthCallback() {
  const [status, setStatus] = useState("confirming");
  const [message, setMessage] = useState("Confirming your email address...");
  const [confirmationType, setConfirmationType] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const confirmSession = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const code = params.get("code");
        const tokenHash = params.get("token_hash") || hashParams.get("token_hash");
        const type = params.get("type") || hashParams.get("type");
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) throw error;
        } else if (tokenHash && type) {
          const allowedTypes = new Set(["signup", "email_change", "magiclink", "invite"]);
          if (!allowedTypes.has(type)) throw new Error("This confirmation link type is not supported here.");
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type,
          });
          if (error) throw error;
        } else {
          throw new Error("This confirmation link is missing its verification code. Please request a new email and try again.");
        }

        if (!cancelled) {
          setConfirmationType(type || "");
          setStatus("success");
          setMessage(
            type === "email_change"
              ? "Your Snows ProAm email has been changed successfully. Return to the app or website and sign in with your new email when asked."
              : "Your Snows ProAm account is ready. Return to the app and tap Log In to continue."
          );
          window.history.replaceState(null, "", "/auth/callback");
        }

        if (type === "email_change") {
          supabase.functions.invoke("email_change_confirmed", {
            body: { source: "auth_callback" },
          }).catch((notifyError) => {
            console.warn("email_change_confirmed notification failed:", notifyError);
          });
        }
      } catch (error) {
        if (!cancelled) {
          setStatus("error");
          setMessage(error?.message || "We could not confirm your email. Please request a new confirmation email in the app and try again.");
        }
      }
    };

    confirmSession();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    dialogRef.current?.focus();
  }, [status]);

  const title =
    status === "success"
      ? confirmationType === "email_change"
        ? "Email changed successfully"
        : "Email confirmed"
      : status === "error"
        ? "Confirmation issue"
        : "Confirming email";
  const badgeText =
    status === "success"
      ? "Completed"
      : status === "error"
        ? "Action needed"
        : "Secure confirmation";

  return (
    <main style={pageStyle}>
      <section
        ref={dialogRef}
        style={dialogStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-callback-title"
        aria-describedby="auth-callback-message auth-callback-support"
        aria-live="polite"
        aria-busy={status === "confirming"}
        tabIndex={-1}
      >
        <div style={{ height: 4, background: "#0C69C8" }} />
        <div style={contentStyle}>
          <img src={LOGO_URL} alt="Snows ProAm" style={logoStyle} />
          <p style={{ margin: "0 0 2px", fontSize: 18, lineHeight: "22px", fontWeight: 800 }}>
            Snows ProAm
          </p>
          <p style={{ margin: "0 0 22px", fontSize: 11, lineHeight: "15px", fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>
            WHERE ATHLETES BELONG
          </p>

          <div style={status === "error" ? errorBadgeStyle : statusBadgeStyle}>{badgeText}</div>
          <h1 id="auth-callback-title" style={{ margin: "0 0 12px", fontSize: 26, lineHeight: "32px", fontWeight: 800 }}>
            {title}
          </h1>
          <p id="auth-callback-message" style={{ margin: "0 auto 18px", maxWidth: 380, color: "#E2E8F0", fontSize: 15, lineHeight: "24px" }}>
            {message}
          </p>
          <div style={{ maxWidth: 400, margin: "0 auto 24px", padding: 16, background: "#101828", border: "1px solid #263044", borderRadius: 12 }}>
            <p id="auth-callback-support" style={{ margin: 0, color: "#FFFFFF", fontSize: 13, lineHeight: "21px" }}>
              If you have any issues, contact{" "}
              <a href="mailto:support@snowsproam.com" style={supportLinkStyle}>
                support@snowsproam.com
              </a>.
            </p>
          </div>
          <Link to="/" style={buttonStyle}>Back to Snows ProAm</Link>
        </div>
      </section>
    </main>
  );
}
